import Link from "next/link";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { DealStepper } from "@/components/deal/DealStepper";
import { DealSupportButton } from "@/components/deal/DealSupportButton";
import { CancelRequestButton } from "@/components/deal/cancel-request-button";
import { resolveDealLifecycle } from "@shared/deal/deal-lifecycle";
import { formatCents } from "@/lib/formatting/currency";
import type { DealSummary } from "@convex/listings/queries";

type DealUxState = DealSummary["uxState"];

interface BuyerDealPanelProps {
  deal: DealSummary;
  history: ReactNode;
  buyAgain?: ReactNode;
}

export function BuyerDealPanel({ deal, history, buyAgain }: BuyerDealPanelProps) {
  const action = primaryActionFor(deal);
  const lifecycle = resolvePanelLifecycle(deal.uxState, deal.isPickup);

  return (
    <div className="space-y-4">
      <DealPanelHeader deal={deal} />

      <DealStepper state={lifecycle} className="text-xs" />

      <p className="text-sm text-muted-foreground">{descriptionFor(deal)}</p>

      <DealPanelActions action={action} />

      {buyAgain}

      <DealPanelFooter deal={deal} />

      {history}
    </div>
  );
}

function DealPanelHeader({ deal }: { deal: DealSummary }) {
  return (
    <div className="space-y-1">
      <div className="text-overline uppercase text-muted-foreground">
        Your deal on this listing
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="text-base font-semibold text-foreground">
          {deal.statusLabel}
        </div>
        {deal.totalCents !== undefined ? (
          <div className="tabular-nums text-sm font-semibold text-foreground">
            {formatCents(deal.totalCents)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DealPanelActions({ action }: { action: PrimaryAction }) {
  return (
    <>
      {action.external ? (
        <Button variant="brand" asChild className="w-full">
          <a href={action.href} target="_blank" rel="noreferrer noopener">
            {action.label}
          </a>
        </Button>
      ) : (
        <Button variant="primary" asChild className="w-full">
          <Link href={action.href}>{action.label}</Link>
        </Button>
      )}

      {action.secondary ? (
        <Button asChild variant="outline" className="w-full">
          <Link href={action.secondary.href}>{action.secondary.label}</Link>
        </Button>
      ) : null}
    </>
  );
}

function DealPanelFooter({ deal }: { deal: DealSummary }) {
  return (
    <div className="flex items-center justify-between gap-2 pt-1">
      <DealSupportButton
        kind={deal.kind === "lead" ? "request" : deal.kind}
        reference={dealReference(deal)}
        variant="ghost"
        size="sm"
      />
      {deal.cancellableLeadId ? (
        <CancelRequestButton
          leadId={deal.cancellableLeadId}
          leadNumber={deal.leadNumber}
          isFreeClaim={deal.recycleOfferType === "free"}
        />
      ) : null}
    </div>
  );
}

interface PrimaryAction {
  label: string;
  href: string;
  external?: boolean;
  secondary?: { label: string; href: string };
}

function inTransitAction(deal: DealSummary): string {
  return deal.isPickup ? "Mark picked up" : "Track delivery";
}

function primaryActionFor(deal: DealSummary): PrimaryAction {
  switch (deal.uxState) {
    case "draft":
      return { label: "Complete your order", href: deal.href };
    case "requested":
      return {
        label: isFreePickupRequest(deal)
          ? "View pickup request"
          : "View request",
        href: deal.href,
      };
    case "vendor_responded": {
      const quoteHref = quoteReviewHref(deal, true);
      if (deal.kind === "quote" && deal.paymentLinkUrl) {
        return {
          label:
            deal.totalCents !== undefined
              ? `Pay ${formatCents(deal.totalCents)}`
              : "Pay now",
          href: deal.paymentLinkUrl,
          external: true,
          secondary: { label: "Review quote", href: quoteReviewHref(deal) },
        };
      }
      return { label: "Review and accept quote", href: quoteHref };
    }
    case "awaiting_vendor":
      return { label: "View status", href: deal.href };
    case "accepted":
      return { label: "View order", href: deal.href };
    case "paid":
      return { label: "Track fulfillment", href: deal.href };
    case "in_transit":
      return { label: inTransitAction(deal), href: deal.href };
    case "delivered":
    case "completed":
      return { label: "View order", href: deal.href };
    case "declined":
    case "expired":
    case "cancelled":
      return { label: "Send a new request", href: "#listing-action-card" };
  }
}

function descriptionFor(deal: DealSummary): string {
  switch (deal.uxState) {
    case "draft":
      return "Your checkout isn't finished.";
    case "requested":
      if (isFreePickupRequest(deal)) {
        return "Pickup request sent. Waiting for the pickup window to be confirmed.";
      }
      if (deal.kind === "quote" && deal.isBuyerSourced) {
        return "Offer sent. Waiting for a response.";
      }
      return "Request sent. Waiting for a quote.";
    case "vendor_responded":
      return "Your quote is ready.";
    case "awaiting_vendor":
      return "Your order is being prepared.";
    case "accepted":
      return "Deal accepted.";
    case "paid":
      return "Payment complete.";
    case "in_transit":
      return deal.isPickup
        ? "The order is ready for pickup."
        : "The order is on the way.";
    case "delivered":
    case "completed":
      return "This order is complete.";
    case "declined":
      if (deal.kind === "quote" && deal.isBuyerSourced) {
        return "This request was declined or withdrawn.";
      }
      return "This request was declined.";
    case "expired":
      return "This quote expired.";
    case "cancelled":
      return "This deal was cancelled.";
  }
}

function isFreePickupRequest(deal: DealSummary): boolean {
  return (
    deal.kind === "quote" &&
    deal.recycleOfferType === "free" &&
    deal.fulfillmentType === "pickup"
  );
}

function quoteReviewHref(deal: DealSummary, startAcceptance = false): string {
  if (deal.kind === "quote" && deal.quoteNumber) {
    return startAcceptance ? `${deal.href}?pay=1` : deal.href;
  }
  return deal.href;
}

function resolvePanelLifecycle(state: DealUxState, isPickup?: boolean) {
  switch (state) {
    case "draft":
      return resolveDealLifecycle({
        quote: { status: "converted" },
        order: { stage: "draft" },
      });
    case "requested":
      return resolveDealLifecycle({ quote: { status: "sent" } });
    case "vendor_responded":
      return resolveDealLifecycle({ quote: { status: "viewed" } });
    case "awaiting_vendor":
      return resolveDealLifecycle({
        quote: { status: "converted" },
        order: { stage: "awaiting_pickup" },
      });
    case "accepted":
    case "paid":
      return resolveDealLifecycle({
        quote: { status: "converted" },
        order: { stage: "awaiting_pickup" },
      });
    case "in_transit":
      return resolveDealLifecycle({
        quote: { status: "converted" },
        order: { stage: "in_transit", isPickup },
      });
    case "delivered":
      return resolveDealLifecycle({
        quote: { status: "converted" },
        order: { stage: "delivered", isPickup },
      });
    case "completed":
      return resolveDealLifecycle({
        quote: { status: "converted" },
        order: { stage: "completed", isPickup },
      });
    case "declined":
      return resolveDealLifecycle({ quote: { status: "rejected" } });
    case "expired":
      return resolveDealLifecycle({ quote: { status: "expired" } });
    case "cancelled":
      return resolveDealLifecycle({ quote: { status: "cancelled" } });
  }
}

function dealReference(deal: DealSummary): string {
  switch (deal.kind) {
    case "lead":
      return deal.leadNumber ?? "RFQ on listing";
    case "quote":
      return deal.quoteNumber ?? "Quote on listing";
    case "order":
      return `Order on listing`;
  }
}
